import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "../Buttons";
import {
  FileInput,
  FormGrid,
  MultiCheckboxGroup,
  SelectInput,
  SelectOption,
  TextArea,
  TextInput,
} from "./FormControls";
import {
  allergySchema,
  AllergyFormValues,
  clinicalScoreSchema,
  ClinicalScoreFormValues,
  documentSchema,
  DocumentFormValues,
  implantSchema,
  ImplantFormValues,
  medicineSchema,
  MedicineFormValues,
  rehabProtocolSchema,
  RehabProtocolFormValues,
  surgerySchema,
  SurgeryFormValues,
} from "./schemas";
import { DocumentFormat, DocumentFormatLabels } from "../../constants";

type SaveHandler<T> = (values: T) => Promise<void>;

const SectionActions: React.FC<{
  isLoading?: boolean;
  onReset: () => void;
  submitLabel?: string;
}> = ({ isLoading, onReset, submitLabel = "Αποθήκευση" }) => (
  <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
    <Button type="button" variant="secondary" onClick={onReset} disabled={isLoading}>
      Καθαρισμός
    </Button>
    <Button type="submit" variant="primary" isLoading={isLoading}>
      {submitLabel}
    </Button>
  </div>
);

export const SurgerySectionForm: React.FC<{
  options: SelectOption[];
  isLoading?: boolean;
  onSave: SaveHandler<SurgeryFormValues>;
}> = ({ options, isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SurgeryFormValues>({
    resolver: zodResolver(surgerySchema),
    defaultValues: {
      surgeryTypeId: 0,
      surgeryDate: "",
      statusDescr: "",
      category: "",
      startTime: "",
      endTime: "",
      preOpDiagnosis: "",
      postOpDiagnosis: "",
      remarks: "",
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSave(values);
        reset();
      })}
    >
      <FormGrid>
        <SelectInput
          label="Τύπος Χειρουργείου"
          required
          options={options}
          error={errors.surgeryTypeId}
          {...register("surgeryTypeId")}
        />
        <TextInput
          label="Ημερομηνία"
          type="date"
          required
          error={errors.surgeryDate}
          {...register("surgeryDate")}
        />
        <TextInput
          label="Κατάσταση"
          required
          placeholder="π.χ. Προγραμματισμένο"
          error={errors.statusDescr}
          {...register("statusDescr")}
        />
        <TextInput
          label="Κατηγορία"
          error={errors.category}
          {...register("category")}
        />
        <TextInput
          label="Ώρα Έναρξης"
          type="time"
          error={errors.startTime}
          {...register("startTime")}
        />
        <TextInput
          label="Ώρα Λήξης"
          type="time"
          error={errors.endTime}
          {...register("endTime")}
        />
        <TextInput
          label="Προεγχειρητική Διάγνωση"
          error={errors.preOpDiagnosis}
          {...register("preOpDiagnosis")}
        />
        <TextInput
          label="Μετεγχειρητική Διάγνωση"
          error={errors.postOpDiagnosis}
          {...register("postOpDiagnosis")}
        />
        <TextArea
          label="Παρατηρήσεις"
          className="md:col-span-2"
          error={errors.remarks}
          {...register("remarks")}
        />
      </FormGrid>
      <SectionActions isLoading={isLoading} onReset={() => reset()} />
    </form>
  );
};

const formatOptions: SelectOption[] = Object.values(DocumentFormat)
  .filter((v): v is number => typeof v === "number" && v !== DocumentFormat.NONE)
  .map((v) => ({
    value: v,
    label: DocumentFormatLabels[v] ?? `Format ${v}`,
  }));

export const DocumentSectionForm: React.FC<{
  documentTypeOptions: SelectOption[];
  surgeryOptions: SelectOption[];
  isLoading?: boolean;
  onSave: SaveHandler<DocumentFormValues>;
}> = ({ documentTypeOptions, surgeryOptions, isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<DocumentFormValues>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      documentTypeId: 0,
      fileName: "",
      format: 0,
      examinationDate: "",
      medicalCenter: "",
      documentRemarks: "",
      patientSurgeryIds: [],
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSave(values);
        reset();
      })}
    >
      <FormGrid>
        <SelectInput
          label="Τύπος Εγγράφου"
          required
          options={documentTypeOptions}
          error={errors.documentTypeId}
          {...register("documentTypeId")}
        />
        <TextInput
          label="Τίτλος Εξέτασης"
          required
          error={errors.fileName}
          {...register("fileName")}
        />
        <SelectInput
          label="Format"
          required
          options={formatOptions}
          error={errors.format}
          {...register("format")}
        />
        <TextInput
          label="Ημερομηνία Εξέτασης"
          type="date"
          error={errors.examinationDate}
          {...register("examinationDate")}
        />
        <TextInput
          label="Ιατρικό Κέντρο"
          error={errors.medicalCenter}
          {...register("medicalCenter")}
        />
        <Controller
          name="file"
          control={control}
          render={({ field }) => (
            <FileInput
              label="Αρχείο"
              required
              name={field.name}
              error={errors.file as any}
              onFileChange={(file) => {
                setValue("file", file as File, { shouldValidate: true });
              }}
            />
          )}
        />
        <Controller
          name="patientSurgeryIds"
          control={control}
          render={({ field }) => (
            <MultiCheckboxGroup
              label="Σχετιζόμενα Χειρουργεία"
              options={surgeryOptions}
              values={field.value ?? []}
              onChange={field.onChange}
              error={errors.patientSurgeryIds as any}
            />
          )}
        />
        <TextArea
          label="Παρατηρήσεις"
          className="md:col-span-2"
          error={errors.documentRemarks}
          {...register("documentRemarks")}
        />
      </FormGrid>
      <SectionActions isLoading={isLoading} onReset={() => reset()} />
    </form>
  );
};

export const ImplantSectionForm: React.FC<{
  options: SelectOption[];
  isLoading?: boolean;
  onSave: SaveHandler<ImplantFormValues>;
}> = ({ options, isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ImplantFormValues>({
    resolver: zodResolver(implantSchema),
    defaultValues: {
      implantId: 0,
      serialNumber: "",
      lotNumber: "",
      anatomicLocation: "",
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSave(values);
        reset();
      })}
    >
      <FormGrid>
        <SelectInput
          label="Μοντέλο Εμφυτεύματος"
          required
          options={options}
          error={errors.implantId}
          {...register("implantId")}
        />
        <TextInput
          label="Ανατομική Θέση"
          required
          error={errors.anatomicLocation}
          {...register("anatomicLocation")}
        />
        <TextInput
          label="Serial Number"
          required
          error={errors.serialNumber}
          {...register("serialNumber")}
        />
        <TextInput
          label="Lot Number"
          required
          error={errors.lotNumber}
          {...register("lotNumber")}
        />
      </FormGrid>
      <SectionActions isLoading={isLoading} onReset={() => reset()} />
    </form>
  );
};

export const MedicineSectionForm: React.FC<{
  medicineOptions: SelectOption[];
  doseOptions: SelectOption[];
  isLoading?: boolean;
  onSave: SaveHandler<MedicineFormValues>;
}> = ({ medicineOptions, doseOptions, isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<MedicineFormValues>({
    resolver: zodResolver(medicineSchema),
    defaultValues: {
      medicineId: 0,
      doseId: 0,
      datePrescribed: "",
      duration: "",
      patMedRemarks: "",
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSave(values);
        reset();
      })}
    >
      <FormGrid>
        <SelectInput
          label="Φάρμακο"
          required
          options={medicineOptions}
          error={errors.medicineId}
          {...register("medicineId")}
        />
        <SelectInput
          label="Δόση"
          required
          options={doseOptions}
          error={errors.doseId}
          {...register("doseId")}
        />
        <TextInput
          label="Ημερομηνία Συνταγογράφησης"
          type="date"
          required
          error={errors.datePrescribed}
          {...register("datePrescribed")}
        />
        <TextInput
          label="Διάρκεια"
          error={errors.duration}
          {...register("duration")}
        />
        <TextArea
          label="Παρατηρήσεις"
          className="md:col-span-2"
          error={errors.patMedRemarks}
          {...register("patMedRemarks")}
        />
      </FormGrid>
      <SectionActions isLoading={isLoading} onReset={() => reset()} />
    </form>
  );
};

export const AllergySectionForm: React.FC<{
  options: SelectOption[];
  isLoading?: boolean;
  onSave: SaveHandler<AllergyFormValues>;
}> = ({ options, isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AllergyFormValues>({
    resolver: zodResolver(allergySchema),
    defaultValues: { allergyId: 0, patAllRemarks: "" },
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSave(values);
        reset();
      })}
    >
      <FormGrid>
        <SelectInput
          label="Αλλεργία"
          required
          options={options}
          error={errors.allergyId}
          {...register("allergyId")}
        />
        <TextArea
          label="Παρατηρήσεις Ασθενούς"
          error={errors.patAllRemarks}
          {...register("patAllRemarks")}
        />
      </FormGrid>
      <SectionActions isLoading={isLoading} onReset={() => reset()} />
    </form>
  );
};

export const ClinicalScoreSectionForm: React.FC<{
  isLoading?: boolean;
  onSave: SaveHandler<ClinicalScoreFormValues>;
}> = ({ isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClinicalScoreFormValues>({
    resolver: zodResolver(clinicalScoreSchema),
    defaultValues: {
      assessmentDate: "",
      vasPainScore: 0,
      mobilityScore: "",
      scoreRemarks: "",
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSave(values);
        reset();
      })}
    >
      <FormGrid>
        <TextInput
          label="Ημερομηνία Αξιολόγησης"
          type="date"
          required
          error={errors.assessmentDate}
          {...register("assessmentDate")}
        />
        <TextInput
          label="VAS Πόνου (0–10)"
          type="number"
          required
          min={0}
          max={10}
          error={errors.vasPainScore}
          {...register("vasPainScore")}
        />
        <TextInput
          label="Βαθμολογία Κινητικότητας"
          required
          error={errors.mobilityScore}
          {...register("mobilityScore")}
        />
        <TextArea
          label="Παρατηρήσεις"
          error={errors.scoreRemarks}
          {...register("scoreRemarks")}
        />
      </FormGrid>
      <SectionActions isLoading={isLoading} onReset={() => reset()} />
    </form>
  );
};

export const RehabProtocolSectionForm: React.FC<{
  options: SelectOption[];
  isLoading?: boolean;
  onSave: SaveHandler<RehabProtocolFormValues>;
}> = ({ options, isLoading, onSave }) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RehabProtocolFormValues>({
    resolver: zodResolver(rehabProtocolSchema),
    defaultValues: {
      rehabId: 0,
      status: "",
      totalSessionsPrescribed: 1,
      sessionsCompleted: 0,
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => {
        await onSave(values);
        reset();
      })}
    >
      <FormGrid>
        <SelectInput
          label="Κέντρο / Πρωτόκολλο"
          required
          options={options}
          error={errors.rehabId}
          {...register("rehabId")}
        />
        <TextInput
          label="Κατάσταση"
          required
          placeholder="π.χ. Ενεργό"
          error={errors.status}
          {...register("status")}
        />
        <TextInput
          label="Συνολικές Συνεδρίες"
          type="number"
          required
          min={1}
          error={errors.totalSessionsPrescribed}
          {...register("totalSessionsPrescribed")}
        />
        <TextInput
          label="Ολοκληρωμένες Συνεδρίες"
          type="number"
          required
          min={0}
          error={errors.sessionsCompleted}
          {...register("sessionsCompleted")}
        />
      </FormGrid>
      <SectionActions isLoading={isLoading} onReset={() => reset()} />
    </form>
  );
};
